import React, { useEffect, useState } from 'react';

const HOUR_MS = 60 * 60 * 1000;
const URGENT_THRESHOLD_MS = 24 * HOUR_MS;

/** Under an hour the minutes matter, so tick every second; above it, every 30s. */
const tickIntervalFor = (remainingMs: number): number =>
  remainingMs < HOUR_MS ? 1000 : 30000;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * "6d 04h", "22h 14m", "9m" — the mock's monospace pill format. Deliberately
 * no "left" suffix: the pill and its ticking dot already read as time
 * remaining, and the extra word breaks the tabular rhythm at 11px monospace.
 * The lower unit is zero-padded so the pill does not change width as it ticks.
 */
export const formatRemaining = (remainingMs: number): string => {
  if (remainingMs <= 0) return 'Expired';
  const totalMinutes = Math.floor(remainingMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${pad(hours)}h`;
  if (hours > 0) return `${hours}h ${pad(minutes)}m`;
  return `${minutes}m`;
};

export const isUrgent = (remainingMs: number): boolean => remainingMs < URGENT_THRESHOLD_MS;

interface Props {
  expiresAt: string;
}

/**
 * The always-visible expiry ticker on the active suggestion.
 *
 * This is the ONLY thing carrying exclusivity on the surface. Per the spec,
 * exclusivity is never stated in words: allocation is per unit of quantity, so
 * a have row with quantity above 1 can back several concurrent suggestions and
 * any copy claiming the cards are reserved would be factually wrong. The
 * countdown communicates urgency without making that claim.
 */
const SuggestionCountdown: React.FC<Props> = ({ expiresAt }) => {
  const expiryMs = new Date(expiresAt).getTime();
  const [remainingMs, setRemainingMs] = useState(() => expiryMs - Date.now());

  useEffect(() => {
    setRemainingMs(expiryMs - Date.now());
    const id = window.setInterval(
      () => setRemainingMs(expiryMs - Date.now()),
      tickIntervalFor(expiryMs - Date.now())
    );
    return () => window.clearInterval(id);
    // Re-arm when the interval bucket changes so the cadence tightens under an hour
  }, [expiryMs, remainingMs < HOUR_MS]); // eslint-disable-line react-hooks/exhaustive-deps

  const urgent = isUrgent(remainingMs);

  return (
    <span
      className={`sugg-countdown${urgent ? ' sugg-countdown--urgent' : ''}`}
      // The value changes on a timer; announcing every tick would be noise.
      aria-live="off"
    >
      {/* The pulsing dot is what carries exclusivity, since it is never
          stated in words. Both dot and text turn urgent below 24 hours. */}
      <span className="sugg-countdown-dot" aria-hidden="true" />
      <span className="sugg-countdown-time">{formatRemaining(remainingMs)}</span>
    </span>
  );
};

export default SuggestionCountdown;
